/* MobileTodayLauncher.jsx — global mobile-only entry point to Today.
 *
 * A calm floating violet pill that sits fixed at the bottom-right on
 * mobile viewports. Hidden on tablets and desktops where the sidebar
 * is already visible. Hidden on the /today route itself (no point
 * launching from the page it opens).
 *
 * Doctrine:
 *   - Single-purpose, never expands, never modal.
 *   - Operational vocabulary: "Today" — nothing else.
 *   - Calm palette (violet), 1px border, gentle shadow, never glow.
 *   - Tap target ≥40px for thumb ergonomics.
 *
 * Mounted once at App.js level so every authenticated mobile screen
 * gets it. Lives above any bottom-nav (z-50) but never above modals.
 */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { CalendarCheck2 } from "lucide-react";

export function MobileTodayLauncher() {
  const { pathname } = useLocation();

  // Hide on Today (no self-link) and on auth / marketing routes where
  // it would be intrusive. iter-195 also hides it on /deal-rooms
  // because the new mobile dark hero is the workspace launcher
  // itself — a redundant Today pill would clutter the surface.
  const hidePrefixes = ["/today", "/login", "/signup", "/verify-email", "/forgot", "/reset", "/onboarding", "/", "/pricing"];
  const isOnAuthRoute = hidePrefixes.some((p) => pathname === p);
  if (pathname.startsWith("/today")) return null;
  if (pathname === "/deal-rooms") return null;
  if (isOnAuthRoute) return null;

  return (
    <Link
      to="/today"
      aria-label="Open Today"
      className="md:hidden fixed bottom-20 right-[68px] z-40 inline-flex items-center gap-2 h-11 pl-3 pr-3.5 rounded-full text-white text-[12.5px] font-bold transition-transform active:scale-95"
      style={{
        background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
        boxShadow: "0 14px 32px -10px rgba(99,102,241,0.6), 0 2px 6px rgba(15,23,42,0.12)",
      }}
      data-testid="mobile-today-launcher"
    >
      <span
        className="w-5 h-5 rounded-full inline-flex items-center justify-center"
        style={{ background: "rgba(255,255,255,0.18)" }}
        aria-hidden
      >
        <CalendarCheck2 size={12} strokeWidth={2.4} />
      </span>
      Today
    </Link>
  );
}

export default MobileTodayLauncher;
